import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { z } from 'zod';
import { useApi, useRequiredContext } from '../hooks';
import { useAppInit } from './appInitContext';
import {
  productDetailTransformer,
  tProductListItem,
  zProductDetailItem,
  tProductDetailItem,
  zProductListItem,
} from './zod';

const zQueryInput = z.object({
  productNumber: z.string(),
  variant: z.string().nullable(),
});

const zContext = z.object({
  getProductDetail: z.function().args(zQueryInput).returns(z.void()),
  productDetail: zProductDetailItem.optional(),
  loading: z.boolean(),
  search: z.string(),
  searchInputHandler: z.function().args(z.string()).returns(z.void()),
  productList: zProductListItem.array(),
  searchForDefaultProducts: z.function().returns(z.void()),
});

type tContext = z.infer<typeof zContext>;
type tQueryInput = z.infer<typeof zQueryInput>;

const ProductContext = createContext<tContext>(undefined);

const ProductProvider: FC = ({ children }) => {
  const api = useApi();
  const { params } = useAppInit();
  const { customerNumber, email, filterBy } = params;
  const [productDetail, setProductDetail] = useState<
    tProductDetailItem | undefined
  >(undefined);
  const [search, setSearch] = useState<string>(undefined);
  const [productList, setProductList] = useState<tProductListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const searchInputHandler = useCallback((searchString: string) => {
    setSearch(searchString);
  }, []);

  const getProductDetail = useCallback(
    async ({ productNumber, variant }: tQueryInput) => {
      setLoading(true);
      const response = await api(
        `products/details?customerNumber=${customerNumber}&email=${email}&productNumber=${productNumber}&variant=${variant}${(filterBy ? `&filterBy=${filterBy}` : '')}`,
        'GET',
        'no-cors',
      );
      const result = productDetailTransformer(response.data.details);
      setProductDetail(result);
      setLoading(false);
      return result;
    },
    [api, customerNumber, email],
  );

  const searchForDefaultProducts = useCallback(async () => {
    setLoading(true);
    setSearch('');
    const response = await api(
      `products/default?customerNumber=${customerNumber}${(filterBy ? `&filterBy=${filterBy}` : '')}`,
      'GET',
      'no-cors',
    );
    if (response?.data?.matches?.length > 0) {
      setProductList(response?.data?.matches);
    } else {
      setProductList([]);
    }
    setLoading(false);
  }, [api, customerNumber]);

  const searchForProduct = useCallback(async () => {
    setLoading(true);
    const response = await api(
      `products/search?customerNumber=${customerNumber}${(filterBy ? `&filterBy=${filterBy}` : '')}&query=${search}`,
      'GET',
      'no-cors',
    );
    if (response?.data?.matches?.length > 0) {
      setProductList(response?.data?.matches);
    } else {
      setProductList([]);
    }
    setLoading(false);
  }, [api, customerNumber, search]);

  useEffect(() => {
    if (search?.length > 2) {
      const typingTimer = setTimeout(searchForProduct, 1000);
      return () => {
        clearTimeout(typingTimer);
      };
    }

    if (search?.length === 0) {
      searchForDefaultProducts();
    }
    return () => null;
  }, [searchForProduct, search]);

  const value = useMemo(
    () => ({
      productDetail,
      getProductDetail,
      loading,
      search,
      searchInputHandler,
      productList,
      searchForDefaultProducts,
    }),
    [
      productDetail,
      getProductDetail,
      loading,
      search,
      searchInputHandler,
      productList,
      searchForDefaultProducts,
    ],
  );

  ProductContext.displayName = 'ProductProvider';
  return (
    <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
  );
};

export const useProductContext = () => useRequiredContext(ProductContext);
export default ProductProvider;
